import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import PreChatMoodModal from "../PreChatMoodModal"; // NEW
import { Compass, Eye, Sprout } from "lucide-react";
import ProviderProfileModal from "../ProviderProfileModal";
import api from "../../api/axios";
import { useLanguage } from "../../context/LanguageContext";
import tracker from "../../utils/sanctuary-tracker";
import { motion, AnimatePresence } from "framer-motion";

export default function ExploreProviders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const isProvider = !!user?.provider;

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [starting, setStarting] = useState(null); // Used for loading state
  const [pendingProviderId, setPendingProviderId] = useState(null); // Used for modal
  const [error, setError] = useState(null);
  const [viewingProfileId, setViewingProfileId] = useState(null);
  const [showMoodModal, setShowMoodModal] = useState(false); // NEW

  useEffect(() => {
    if (isProvider) {
      setLoading(false);
      return;
    }

    const fetchMatches = async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/categories/matches');

        setProviders(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setError("Could not load providers at this time.");
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchMatches();
  }, [user, isProvider]);

  const initiateChat = (providerId) => {
    tracker.track('initiate_chat', { providerId });
    setPendingProviderId(providerId);
    setShowMoodModal(true);
  };

  const handleMoodConfirm = (mood) => {
    setShowMoodModal(false);
    if (pendingProviderId) {
      tracker.track('submit_pre_chat_mood', { mood });
      startConversation(pendingProviderId, mood);
    }
  };

  const startConversation = async (providerId, mood) => {
    try {
      setStarting(providerId);

      const { data } = await api.post('/conversations/start', { providerId });

      // Pass mood in URL
      let url = `/chat?cid=${data.conversationId}`;
      if (mood && mood !== "Skipped") {
        url += `&mood=${encodeURIComponent(mood)}`;
      }

      navigate(url);
    } catch (err) {
      alert(err.message);
    } finally {
      setStarting(null);
      setPendingProviderId(null);
    }
  };

  /* ================= PROVIDER VIEW ================= */
  if (isProvider) {
    return (
      <div className="bg-white p-4 rounded-xl shadow h-full">
        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <Eye className="w-5 h-5 text-indigo-500" /> Your Visibility
        </h2>
        <p className="text-sm text-gray-600">
          Your profile is discoverable by seekers based on your
          selected categories.
        </p>
        <p className="text-sm text-gray-500 mt-2 italic">
          Providers do not browse other providers.
        </p>
      </div>
    );
  }

  /* ================= SEEKER VIEW ================= */
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow h-full relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Compass className="w-5 h-5 text-indigo-500" /> {t('dash_explore_providers')}
        </h2>

        <button
          onClick={() => setOpen(true)}
          className="text-sm px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition"
        >
          {t('dash_view_all')} →
        </button>
      </div>

      {loading && (
        <div className="text-sm text-gray-500 italic">
          {t('dash_explore_loading')}
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-red-500 italic">{error}</p>
      )}

      {!loading && !error && providers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
          <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <Sprout className="w-8 h-8 text-green-500" />
          </div>
          <p className="text-gray-600 dark:text-gray-300 font-medium mb-1">
            {t('dash_explore_empty')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
            {t('dash_explore_empty_sub')}
          </p>
        </div>
      )}

      {/* Preview List (Dashboard widget) */}
      <div className="mt-2 -mx-2 sm:mx-0 overflow-x-hidden">
        <ul className="space-y-4 px-2 sm:px-0">
          {providers.slice(0, 3).map((p) => (
            <li key={p.id} className="p-4 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800/50 hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-[15px] text-gray-800 dark:text-gray-100 truncate pr-2">{p.name || "Compassionate Guide"}</div>
                {/* Certified tags temporarily removed */}
              </div>
              <div className="flex gap-2.5">
                <button
                  onClick={() => setViewingProfileId(p.id)}
                  className="flex-1 py-2 text-xs font-medium rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-100 dark:border-gray-600 transition-colors"
                >
                  {t('dash_view_profile')}
                </button>
                <button
                  onClick={() => initiateChat(p.id)}
                  className="flex-1 py-2 text-xs font-bold rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 border border-indigo-100/50 dark:border-indigo-800/50 transition-colors"
                >
                  {t('dash_chat_now')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Modal for FULL LIST */}
      <AnimatePresence>
        {open && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          >
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-3xl shadow-2xl p-6 relative border border-gray-100 dark:border-gray-800"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-2 rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
              >
                ✕
              </button>
              <h3 className="text-xl font-display font-bold mb-6 text-gray-900 dark:text-white flex items-center gap-3">
                 <Compass className="w-6 h-6 text-indigo-500" /> {t('dash_explore_modal_title')}
              </h3>

              <ul className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                {providers.length === 0 ? (
                   <div className="py-20 text-center flex flex-col items-center">
                      <Sprout className="w-10 h-10 text-green-500/40 mb-4 animate-float" />
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                         Breathe in...Hold.... Breathe out
                      </p>
                   </div>
                ) : (
                  providers.map((p) => (
                    <li key={p.id} className="p-4 rounded-2xl border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-300">
                      <div className="flex flex-col gap-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-bold text-[16px] text-gray-900 dark:text-gray-100">{p.name || "Compassionate Guide"}</div>
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => setViewingProfileId(p.id)}
                            className="flex-1 py-2.5 text-xs font-bold rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-all active:scale-[0.98]"
                          >
                            {t('dash_view_profile')}
                          </button>
                          <button
                            disabled={starting === p.id}
                            onClick={() => initiateChat(p.id)}
                            className="flex-1 py-2.5 text-xs font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all active:scale-[0.98]"
                          >
                            {t('dash_explore_msg_btn')}
                          </button>
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* NEW: Profile Details Modal */}
      {viewingProfileId && (
        <ProviderProfileModal
          providerId={viewingProfileId}
          onClose={() => setViewingProfileId(null)}
        />
      )}

      {/* Mood Check Modal */}
      <PreChatMoodModal
        isOpen={showMoodModal}
        onClose={() => setShowMoodModal(false)}
        onConfirm={handleMoodConfirm}
      />
    </div>
  );
}
